<template>
  <SbDropdownItem
    class="sb-menu-item"
    :disabled="isDisabled"
    :negative="type === 'negative'"
    :label="label"
    :icon="icon"
    :description="auxText"
    :tag="as"
    :to="to"
    v-on="{
      click: handleClick,
      keydown: handleKeyDown,
      mouseenter: () => emit('mouseenter'),
      mouseleave: () => emit('mouseleave'),
      focus: () => emit('focus'),
      blur: () => emit('blur'),
    }"
    @click="handleClick"
    @keydown="handleKeyDown"
  >
    <slot v-if="$slots.default" />
  </SbDropdownItem>
</template>

<script lang="ts" setup>
import { SbDropdownItem } from '../Dropdown'
import { useMenu } from './utils'
import type { IconName } from '../Icon'

type MenuItemProps = {
  /**
   * The item description. It is a small text placed below the label
   */
  auxText?: string

  /**
   * The icon of the item
   */
  icon?: IconName

  /**
   * Controls if the item is disabled
   */
  isDisabled?: boolean

  /**
   * The label of the item
   */
  label?: string

  /**
   * The type of the item. If negative, the item will have a negative style
   */
  type?: 'negative'

  /**
   * Define the tag of the menu item
   */
  as?: string

  /**
   * Define the to attribute for the dropdown item. Should be used along with router-link
   */
  to?: Record<string, unknown>
}

const props = defineProps<MenuItemProps>()

const emit = defineEmits([
  'click',
  'keydown',
  'mouseenter',
  'mouseleave',
  'focus',
  'blur',
])

const { context } = useMenu()

const handleClick = (event: MouseEvent) => {
  const { closeMenu, preventClose } = context.value

  emit('click', event)

  if (props.isDisabled) {
    event.preventDefault()
    event.stopPropagation()
  }

  if (!preventClose) {
    closeMenu()
  }
}

const handleKeyDown = (event: KeyboardEvent) => {
  const { closeMenu } = context.value

  emit('keydown', event)

  if (props.isDisabled) return

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()

    emit('click')

    closeMenu()
  }
}
</script>
